/**
 * v-focus-trap — accessibility helper for modal dialogs.
 *
 * Applied to a modal root element (the `.modal` container), it provides the
 * three basics every dialog needs and that the UX audit found missing:
 *   1. Autofocus — on insert, focuses the first focusable element inside the
 *      modal (or the modal container itself if none), so keyboard/AT users
 *      land inside the dialog.
 *   2. Focus trap — Tab / Shift+Tab cycle within the modal instead of leaking
 *      to the page behind the backdrop.
 *   3. Escape to close — pressing Escape invokes the view's own close handler.
 *
 * The close handler is passed as the directive value so behavior stays owned by
 * the view (no assumptions about how each modal closes):
 *
 *   <div class="modal" role="dialog" aria-modal="true" v-focus-trap="closeFn">
 *
 * When the modal is removed (v-if) focus returns to the element that had it
 * before the modal opened, where feasible.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusable(el) {
  return Array.from(el.querySelectorAll(FOCUSABLE)).filter(
    (n) => n.offsetParent !== null || n === document.activeElement
  );
}

function onKeydown(event) {
  const el = this;
  if (event.key === 'Escape' || event.key === 'Esc') {
    const close = el.__focusTrapClose;
    if (typeof close === 'function') {
      event.preventDefault();
      close(event);
    }
    return;
  }
  if (event.key !== 'Tab') return;

  const items = focusable(el);
  if (!items.length) {
    // Nothing focusable inside — keep focus on the container.
    event.preventDefault();
    el.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey) {
    if (active === first || active === el || !el.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last) {
    event.preventDefault();
    first.focus();
  }
}

export default {
  inserted(el, binding) {
    el.__focusTrapClose = binding.value;
    el.__focusTrapPrev =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');

    el.__focusTrapKeydown = onKeydown.bind(el);
    el.addEventListener('keydown', el.__focusTrapKeydown);

    // Autofocus the first focusable element (or the container) once painted.
    el.__focusTrapRaf = window.requestAnimationFrame(() => {
      const items = focusable(el);
      (items[0] || el).focus();
    });
  },
  update(el, binding) {
    // Keep the close handler reference current if the view rebinds it.
    el.__focusTrapClose = binding.value;
  },
  unbind(el) {
    if (el.__focusTrapKeydown) el.removeEventListener('keydown', el.__focusTrapKeydown);
    if (el.__focusTrapRaf) window.cancelAnimationFrame(el.__focusTrapRaf);
    const prev = el.__focusTrapPrev;
    if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
      prev.focus();
    }
    delete el.__focusTrapClose;
    delete el.__focusTrapKeydown;
    delete el.__focusTrapPrev;
    delete el.__focusTrapRaf;
  },
};
